/**
 * H14: join a sect in its own map. Either senior of `sect-<id>` offers "เข้าร่วมสำนัก" with the motto and join line.
 * Six sects are schools (data/schools.ts): the dialog carries `joinSchool`, so the World's own join (title, first art,
 * neigong) runs and the client needs nothing new. The two without a school yet (bamboo-tide, snow-pine) are joined
 * here: the first art is learned and a scene mark `sect-join:<id>` records the sect. TODO-polish: real leave/cooldown
 * rules (docs/design/C0_SECTS.md §4); the text below is a placeholder.
 */
import { SECT_NEIGONG } from '../data/neigong';
import { SCHOOLS } from '../data/schools';
import { SECT_TABLE } from '../data/layouts/sects';
import type { Entity } from '../world';
import type { PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Sects with no school yet: motto, join line, first art (placeholder arts). */
export const SECT_EXTRA: Readonly<Record<string, { motto: string; join: string; art: string }>> = {
  'bamboo-tide': { motto: 'ไผ่โอนตามคลื่น แต่รากไม่เคยลอย', join: 'ถือไม้พลองของคลื่นไผ่ไว้ แล้วเรียนจังหวะน้ำจากข้า', art: 'tide-staff' },
  'snow-pine': { motto: 'สนใต้หิมะ ไม่หักเพราะไม่เคยรีบ', join: 'ยืนในหิมะให้นิ่งก่อน แล้วข้าจะสอนกระบี่สนให้', art: 'snow-sword' },
};

export const LEAVE_RULE_TEXT = 'เจ้ามีสำนักอยู่แล้ว ออกจากสำนักได้ แต่ต้องรอช่วงหนึ่งก่อนเข้าสำนักใหม่ (กติกาละเอียดจะมาภายหลัง)';
const MARK = 'sect-join:';
export const sectMark = (id: string): string => `${MARK}${id}`;

/** The sect the player belongs to (a school, else the `sect-join:` scene mark), or null. */
export function playerSect(pr: { school?: string | null; scenes: string[] }): string | null {
  const m = pr.scenes.find((s) => s.startsWith(MARK));
  return pr.school ?? (m ? m.slice(MARK.length) : null);
}

/** The sect id when `zone` is a `sect-<id>` zone of a known sect, else null. */
export function zoneSect(zone: string): string | null {
  return zone.startsWith('sect-') && SECT_TABLE.some((s) => `sect-${s.id}` === zone) ? zone.slice(5) : null;
}

export class SectJoinSystem implements WorldSystem {
  readonly name = 'sectJoin';
  constructor(private readonly h: SystemHost) {}

  private sectOf(npc: Entity): string | null {
    const id = zoneSect(this.h.world.progress.location as string);
    const r = id ? SECT_TABLE.find((s) => s.id === id) : undefined;
    return r && r.senior.includes(npc.name) ? r.id : null;
  }

  private current(): string | null {
    return playerSect(this.h.world.progress);
  }

  talk(npc: Entity): boolean {
    const id = this.sectOf(npc);
    if (!id) return false;
    const sc = SCHOOLS[id];
    const ex = SECT_EXTRA[id];
    const mine = this.current();
    const motto = sc?.motto ?? ex?.motto ?? '';
    const text =
      mine === id
        ? `${sc?.memberText ?? 'เจ้าเป็นศิษย์สำนักนี้แล้ว ฝึกต่อไปเถิด'}`
        : mine
          ? LEAVE_RULE_TEXT
          : `“${motto}” ${sc?.joinText ?? ex?.join ?? ''}`;
    this.h.emit({ type: 'dialog', npcId: npc.id, text, action: mine ? null : 'joinSchool', schoolId: id, label: 'เข้าร่วมสำนัก' });
    return true;
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'joinSchool' || SCHOOLS[cmd.id]) return false;
    const ex = SECT_EXTRA[cmd.id];
    if (!ex) return false;
    const w = this.h.world;
    const pr = w.progress;
    if (this.current()) {
      this.h.notice(LEAVE_RULE_TEXT);
      return true;
    }
    pr.scenes.push(sectMark(cmd.id));
    if (!pr.arts.includes(ex.art)) pr.arts.push(ex.art);
    pr.activeArt = ex.art;
    // H18: joining gives the sect's tier-1 neigong (the six schools get theirs from the World's own join).
    const t1 = SECT_NEIGONG[cmd.id]?.[0];
    if (t1) pr.neigong[t1] ??= 1;
    this.h.emit({ type: 'learnedArt', art: ex.art });
    this.h.emit({ type: 'progressChanged' });
    return true;
  }
}
